import { useState } from "react";
import { Play } from "lucide-react";
import defaultHeroImage from "@/assets/hero-default.jpg";
import { useHeroSettings } from "@/hooks/useHeroSettings";
import { SponsoredVideoModal } from "@/components/SponsoredVideoModal";
import { optimizeImage } from "@/lib/imageUrl";

interface HeroMediaProps {
  /** Override the source (used in admin live preview) */
  override?: {
    mode: "image" | "video";
    image_url: string | null;
    youtube_video_id: string | null;
    cover_image_url: string | null;
    alt_text: string | null;
  };
}

export const HeroMedia = ({ override }: HeroMediaProps) => {
  const { settings } = useHeroSettings();
  const [open, setOpen] = useState(false);

  const source = override ?? settings;
  const mode = source?.mode ?? "image";
  const altText =
    source?.alt_text ||
    "Families with babies and toddlers enjoying activities together";

  if (mode === "video" && source?.youtube_video_id) {
    const cover = optimizeImage(source.cover_image_url, { width: 1200, quality: 70 }) || defaultHeroImage;
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group relative block w-full rounded-2xl overflow-hidden shadow-soft focus:outline-none focus:ring-2 focus:ring-white/60"
          aria-label="Play hero video"
        >
          <img
            src={cover}
            alt={altText}
            width={1600}
            height={900}
            fetchPriority="high"
            decoding="async"
            className="w-full h-auto object-cover aspect-[16/9]"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <Play className="w-7 h-7 text-primary ml-1" fill="currentColor" />
            </div>
          </div>
        </button>
        <SponsoredVideoModal
          open={open}
          onOpenChange={setOpen}
          videoId={source.youtube_video_id}
          title={altText}
        />
      </>
    );
  }

  const imgSrc = optimizeImage(source?.image_url, { width: 1200, quality: 70 }) || defaultHeroImage;
  return (
    <div className="rounded-2xl overflow-hidden shadow-soft aspect-[16/9]">
      <img
        src={imgSrc}
        alt={altText}
        width={1600}
        height={900}
        fetchPriority="high"
        decoding="async"
        className="w-full h-full object-cover"
      />
    </div>
  );
};
